import { analytics } from 'analytics'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { paveApi } from 'api'
import PageLoader from 'components/common/Loader/PageLoader'
import NavigationDropdownMenu from 'components/common/NavigationDropdownMenu/NavigationDropdownMenu'
import { LOGOUT_CLICKED } from 'constants/track.constants'
import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSNavBar from 'designSystem/components/PDSNavBar/PDSNavBar'
import PDSPageContentArea from 'designSystem/components/PDSPageContentArea/PDSPageContentArea'
import { COLOR } from 'designSystem/foundations/constants/color'
import { SIZE } from 'designSystem/foundations/constants/size'
import { signOut } from 'store/authSlice'
import { useAppDispatch, useAppSelector } from 'store/storeHooks'
import { resetUser } from 'store/userSlice'

type Props = {
  children: React.ReactNode
  currentOrganization?: string
  footer?: React.ReactNode
  hideMenu?: boolean
  showLoader?: boolean
  showNewLayout?: boolean
  subHeader?: React.ReactNode
}

function PageLayout({
  children,
  currentOrganization,
  footer,
  hideMenu,
  showLoader = false,
  showNewLayout,
  subHeader,
}: Props) {
  const {
    firstName,
    isViewOnly,
    lastName,
    managementOrganizationMemberships,
    members,
  } = useAppSelector((state) => state.user)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const onLogout = () => {
    analytics.track(LOGOUT_CLICKED)
    dispatch(signOut())
    dispatch(resetUser())
    dispatch(paveApi.util.resetApiState())
    navigate('/')
  }

  const userName = `${firstName} ${lastName}`

  const menuItems = [
    {
      action: () => navigate('/settings'),
      icon: 'cog',
      name: 'Settings',
    },
    {
      action: () => navigate('/connected-accounts'),
      icon: 'link',
      name: 'Connected Accounts',
    },
    {
      action: () => navigate('/investment-templates'),
      icon: 'pencil',
      name: 'Investment Templates',
    },
    {
      action: () => navigate('/buy-lists'),
      icon: 'clipboard',
      name: 'Approved Buy Lists',
    },
    {
      action: onLogout,
      icon: 'logout',
      name: 'Log out',
      color: COLOR.alert300,
    },
  ]

  return (
    <PDSBox height="100vh" margin="0 auto">
      <PDSNavBar
        rightContent={
          <NavigationDropdownMenu
            menuItems={menuItems}
            menuName={userName}
            managementOrganizationMemberships={
              managementOrganizationMemberships
            }
            members={members}
            currentOrganization={currentOrganization}
            isViewOnly={isViewOnly}
          />
        }
        showRightContent={!hideMenu}
      />
      {subHeader && subHeader}
      {/* TODO: Move the Breadcrumb into the component */}
      {/*    Create a Redux store to handle path state */}

      {showNewLayout ? (
        <>
          <PDSPageContentArea minHeight="80%">
            {showLoader ? <PageLoader /> : children}
          </PDSPageContentArea>
          {!showLoader && <PDSBox minHeight="4rem">{footer}</PDSBox>}
        </>
      ) : (
        <PDSBox paddingBottom={SIZE.s24}>
          {showLoader ? <PageLoader /> : children}
        </PDSBox>
      )}
    </PDSBox>
  )
}

export default PageLayout
